import { useMetadata, useSelf } from 'App/components/hooks/appHooks';
import {
	Feature,
	Product,
	useFeatureAvailable,
	useProductAvailable,
	useSoftDeployAccess
} from 'App/components/hooks/featureHelper';
import { lazy } from 'react';

const MarketRejectList = lazy(() => import('./subPages/MarketRejectList/MarketRejectList'));
const AdminEmailSignatures = lazy(() => import('./subPages/AdminEmailSignatures/AdminEmailSignatures'));
const AdminNotifications = lazy(() => import('./subPages/AdminNotifications/AdminNotifications'));
const AdminProfile = lazy(() => import('./subPages/AdminProfile/AdminProfile'));
const AdminTriggers = lazy(() => import('./subPages/AdminTriggers/AdminTriggers'));
const BookedFirstAppointmentGoals = lazy(
	() => import('Components/Admin/Goals/BookedFirstAppointmentGoals/BookedFirstAppointmentGoals')
);
const CustomerSupport = lazy(() => import('./subPages/CustomerSupport/CustomerSupport'));
const AdminDashboard = lazy(() => import('./subPages/AdminDashboard/AdminDashboard'));
const PortedImports = lazy(() => import('./subPages/Imports'));
const PortedImport = lazy(() => import('./subPages/Import'));
const AdminTicketRules = lazy(() => import('./subPages/CustomerSupport/AdminTicketRules'));
const PriceLists = lazy(() => import('Components/Admin/PriceLists'));
const AdminEditTicketRule = lazy(() => import('./subPages/CustomerSupport/AdminEditTicketRule'));
const AdminTicketFields = lazy(() => import('./subPages/CustomerSupport/AdminTicketFields'));
const AdminUsersAndRoles = lazy(() => import('./subPages/AdminUsersAndRoles/AdminUsersAndRoles'));
const VisitSettings = lazy(() => import('./subPages/VisitSettings/VisitSettings'));
const AdminActiveInvites = lazy(() => import('./subPages/AdminActiveInvites/AdminActiveInvites'));
const ProjectPlanTemplate = lazy(() => import('Components/Admin/ProjectPlanTemplate'));
const BookedAppointmentGoals = lazy(() => import('Components/Admin/Goals/BookedAppointmentGoals'));
const Salesboard = lazy(() =>
	import('Components/Admin/Salesboard').then(m => ({
		default: m.AdminListSalesboard
	}))
);
const EasyBookingSettings = lazy(() => import('Components/Admin/EasyBookingSettingsV2/Settings/EasyBookingSettings'));
const AdminPipelineGoals = lazy(() => import('Components/Admin/Goals/PipelineGoals'));
const UserDefinedObjects = lazy(() => import('./subPages/UserDefinedObjects'));
const LoginStats = lazy(() => import('Components/Admin/LoginStats').then(m => ({ default: m.PortedLoginStats })));
const Domains = lazy(() => import('Components/Admin/Domains').then(m => ({ default: m.PortedAdminDomains })));
const JourneyStatuses = lazy(() =>
	import('Components/Admin/JourneyStatuses/JourneyStatuses').then(m => ({ default: m.WrappedJourneyStatuses }))
);
const SalesProcess = lazy(() =>
	import('Components/Admin/SalesProcess/List/SalesProcessList').then(m => ({ default: m.PortedSalesProcess }))
);
const SalesboardCard = lazy(() =>
	import('Components/Admin/SalesboardCard').then(m => ({
		default: m.AdminSalesboardCard
	}))
);
const ProjectPlanWorkProcess = lazy(() => import('Components/Admin/ProjectPlanWorkProcess'));
const projectPlanPriorityAndStatus = lazy(() => import('Components/Admin/ProjectPlanPriorityAndStatus'));
const AdminTicketResponseTemplates = lazy(() => import('./subPages/CustomerSupport/AdminTicketResponseTemplates'));
const AdminAnonymization = lazy(() => import('./subPages/AdminAnonymization/AdminAnonymization'));
const AdminFields = lazy(() => import('../AdminFields'));
const ActivityTypes = lazy(() => import('./subPages/ActivityTypes'));
const AdminScripts = lazy(() => import('./subPages/AdminScripts/AdminScripts'));
const AdminScript = lazy(() => import('./subPages/AdminScript/AdminScript'));
const OrderStages = lazy(() => import('../OrderStages'));
const AdminLooker = lazy(async () => import('Components/Admin/Looker').then(m => ({ default: m.WrappedLooker })));
const ProspectingSignals = lazy(() => import('Components/Admin/ProspectingSignalsSettings/ProspectingSignals'));
const EditAutomation = lazy(() => import('./subPages/EditAutomation/EditAutomation'));
const AdminApiKeys = lazy(() => import('./subPages/AdminApiKeys/AdminApiKeys'));
const JourneyEditor = lazy(() =>
	import('Components/Admin/JourneyEditor/JourneyEditor').then(m => ({ default: m.PortedJourneyEditor }))
);
const MailSettings = lazy(() => import('./subPages/MailSettings/MailSettings'));
const Admin2fa = lazy(() => import('./subPages/Admin2fa/Admin2fa'));
const ListAutomations = lazy(() => import('./subPages/ListAutomations/ListAutomations'));
const SharedViews = lazy(() =>
	import('Components/Admin/SharedViews/SharedViews').then(m => ({ default: m.SharedViewsAdminPorted }))
);
const AdminCurrencies = lazy(() => import('App/pages/AdminCurrencies'));
const AdminBilling = lazy(() =>
	import('Components/Billing/BillingRoot').then(module => ({ default: module.PortedAdminBilling }))
);
const AdminDocumentTemplates = lazy(() => import('./subPages/AdminDocumentTemplates/AdminDocumentTemplates'));
const AdminIntegrations = lazy(() => import('./subPages/AdminIntegrations/AdminIntegrations'));
const AdminProducts = lazy(() => import('./subPages/AdminProducts/AdminProducts'));
const Reminders = lazy(() => import('Components/Admin/Reminders/Reminders'));
const AdminConfigureIntegration = lazy(() => import('./subPages/AdminConfigureIntegration/AdminConfigureIntegration'));
const MailSignature = lazy(() => import('Components/MailSignature/MailSignatureRoot'));
const AdminPhoneCallGoals = lazy(() => import('Components/Admin/Goals/PhonecallGoals'));
const AdminCompletedAppointmentGoals = lazy(() => import('Components/Admin/Goals/CompletedAppointmentGoals'));
const AdminAgreements = lazy(() => import('./subPages/AdminAgreements/AdminAgreements'));
const Brands = lazy(() => import('Components/Admin/Brands').then(module => ({ default: module.PortedBrands })));
const DynamicLinks = lazy(() => import('App/pages/DynamicLinks/DynamicLinks'));
const DeleteLog = lazy(() => import('App/pages/DeleteLog/DeleteLog'));
const AdminSalesBoard = lazy(() =>
	import('Components/Admin/EditSalesBoard/EditSalesBoard').then(module => ({
		default: module.PortedAdminEditSalesboard
	}))
);
const AdminSalesQuota = lazy(() => import('./subPages/AdminSalesQuota'));
const OptIns = lazy(() => import('App/upsales/common/components/react/admin/pages/optIns'));
const AdminOptOut = lazy(() => import('./subPages/AdminOptOut/AdminOptOut'));
const CompletedFirstAppointmentGoals = lazy(
	() => import('Components/Admin/Goals/CompletedFirstAppointmentGoals/CompletedFirstAppointmentGoals')
);
const AdminEditIntegration = lazy(() => import('./subPages/AdminEditIntegration/AdminEditIntegration'));
const AccountProfile = lazy(() => import('App/pages/AccountProfile'));
const AdminIpBlock = lazy(() => import('./subPages/AdminIpBlock'));
const AdminSharedEasyBooking = lazy(
	() => import('Components/Admin/EasyBookingSettingsV2/SharedEasyBooking/SharedEasyBooking')
);
const AdminSharedEasyBookingSettings = lazy(
	() => import('Components/Admin/EasyBookingSettingsV2/SharedSettings/SharedEasyBookingSettings')
);
const AdminActivityQuota = lazy(() => import('./subPages/AdminActivityQuota/AdminActivityQuota'));
const OneOff = lazy(() => import('./subPages/OneOffQuota/OneOffQuota'));
const SalesMarginQuota = lazy(() => import('./subPages/SalesMarginQuota/SalesMarginQuota'));
const SubscriptionQuota = lazy(() => import('./subPages/SubscriptionQuota/SubscriptionQuota'));
const ProductQuota = lazy(() => import('./subPages/ProductQuota/ProductQuota'));

const AdminAccountsSettings = lazy(() => import('./subPages/AdminAccountSettings/AdminAccountSettings'));
const AdminEditTrigger = lazy(() => import('./subPages/AdminTriggers/EditTrigger'));
const AdminAppointmentEditor = lazy(() =>
	import('Components/Admin/EasyBookingSettingsV2/Editor/EasyBookingSettingsPage').then(m => ({
		default: m.PortedEasyBookingSettingsPage
	}))
);
const Salesboards = lazy(() => import('./subPages/Salesboards/Salesboards'));
const AdminTicketAutoReplyTemplates = lazy(() => import('./subPages/CustomerSupport/AdminTicketAutoReplyTemplates'));
const UserRoute = lazy(() => import('./subPages/EditUser/EditUser'));

const CreditMonitoring = lazy(() => import('./subPages/CreditMonitoring/CreditMonitoring'));

const AdminTicketForms = lazy(() => import('./subPages/CustomerSupport/AdminTicketForms'));

const AdminAIPrompts = lazy(() => import('./subPages/AdminAIPrompts'));
const AdminMcpSecurity = lazy(() => import('./subPages/AdminMcpSecurity'));
const AdminOpportunityRisks = lazy(() => import('./subPages/AdminOpportunityRisks/AdminOpportunityRisks'));
const AdminCategories = lazy(() => import('./subPages/AdminCategories'));

const useAdminRoutes = () => {
	const isAdmin = useSelf()?.administrator;
	const isMailAdmin = useSelf()?.userParams.mailAdmin;
	const isTeamLeader = useSelf()?.teamLeader;
	const isBillingAdmin = useSelf()?.billingAdmin;

	const isSalesProcessAvailable = useFeatureAvailable(Feature.SALES_PROCESS);
	const hasSupportAccess = useFeatureAvailable(Feature.CUSTOMER_SUPPORT);
	const hasProjectPlan = useFeatureAvailable(Feature.PROJECT_PLAN);
	const hasCrm = useProductAvailable(Product.CRM);
	const hasProspectingPro = useFeatureAvailable(Feature.PROSPECTING_PRO);
	const hasAiFeature = useFeatureAvailable(Feature.AI);
	const hasProspectingSignals = useFeatureAvailable(Feature.PROSPECTING_SIGNALS);
	const hasApiKeys = useFeatureAvailable(Feature.API_KEYS);
	const automationsAvailable = useFeatureAvailable(Feature.AUTOMATIONS);
	const hasSmsAuth = useFeatureAvailable(Feature.SMSAUTH);
	const journeyStatusAvailable = useFeatureAvailable(Feature.JOURNEY_STATUS);
	const hasMailSignature = useFeatureAvailable(Feature.MAIL_SIGNATURE);
	const hasSharedViews = useFeatureAvailable(Feature.SHARED_VIEWS);
	const hasBrokenFiscalYearAccess = useFeatureAvailable(Feature.BROKEN_FISCAL_YEAR);
	const hasOptIn = useFeatureAvailable(Feature.OPT_IN);
	const hasRecurringOrder = useFeatureAvailable(Feature.RECURRING_ORDER);
	const hasStagesAdminFeature = useFeatureAvailable(Feature.STAGES_ADMIN);
	const hasTriggersFeature = useFeatureAvailable(Feature.TRIGGERS);
	const hasBrandsFeature = useFeatureAvailable(Feature.BRANDS);
	const metadata = useMetadata();
	const hasLookerFeature = useFeatureAvailable(Feature.LOOKER) || !!metadata?.params?.LookerDashboardExeptionIds;

	const hasGoals = useSoftDeployAccess('GOALS');
	const hasToDo = useSoftDeployAccess('TODO_LIST');
	const shouldShowOldActivityQuota = !(hasGoals && hasToDo);

	const isTeamleader = useSelf()?.teamLeader;
	const hasImportsAccess = useSoftDeployAccess('PORTED_ROUTE_IMPORTS');
	const hasCompletedFirstAppointmentGoalsAcess = Tools.FeatureHelper.hasSoftDeployAccess('GOALS');
	const hasConfigureIntegrationAccess = useSoftDeployAccess('REACT_ADMIN_CONFIGURE_INTEGRATION');
	const hasNewFieldsAccess = useSoftDeployAccess('NEW_FIELDS');
	const hasDynamicLinks = useFeatureAvailable(Feature.DYNAMIC_LINKS);
	const isSupportAdmin = useSelf()?.supportAdmin;
	const isProjectAdmin = useSelf()?.projectAdmin;
	const hasProjectAdminAccess = isProjectAdmin;
	const hasAdminUserAccess = useSoftDeployAccess('PORTED_ROUTE_USER');
	const hasOpportunityRisks = useFeatureAvailable(Feature.OPPORTUNITY_RISKS);
	const hasMcpConnections = useFeatureAvailable(Feature.MCP_CONNECTIONS);
	const hasMcpSoftDeploy = useSoftDeployAccess('MCP_CONNECTIONS');

	const routes = [
		{
			path: '/admin/customer-support/rules',
			Component: AdminTicketRules,
			oldRouteState: 'ticketRules',
			hasAccess: hasSupportAccess,
			exact: true
		},
		{
			path: '/admin/customer-support/rules/:id',
			Component: AdminEditTicketRule,
			oldRouteState: 'ticketRules',
			hasAccess: hasSupportAccess
		},
		{
			path: '/admin/customer-support/forms',
			Component: AdminTicketForms,
			oldRouteState: 'ticketForms',
			hasAccess: hasSupportAccess && (isSupportAdmin || isAdmin)
		},
		{
			path: '/admin/customer-support/verify',
			Component: CustomerSupport,
			oldRouteState: 'customerSupport',
			hasAccess: hasSupportAccess
		},
		{
			path: '/admin/domains',
			Component: Domains,
			oldRouteState: 'domains',
			hasAccess: isAdmin || isMailAdmin
		},
		{
			path: '/admin/salesprocesses',
			Component: SalesProcess,
			oldRouteState: 'salesprocesses',
			hasAccess: isSalesProcessAvailable && isAdmin && hasCrm
		},
		{
			path: '/admin/project-plan/workprocess',
			Component: ProjectPlanWorkProcess,
			oldRouteState: 'projectPlanWorkProcess',
			hasAccess: hasProjectPlan
		},
		{
			path: '/admin/project-plan/priorityandstatus',
			Component: projectPlanPriorityAndStatus,
			oldRouteState: 'projectPlanPriorityAndStatus',
			hasAccess: hasProjectPlan
		},
		{
			path: '/admin/project-plan/templates',
			Component: ProjectPlanTemplate,
			oldRouteState: 'projectPlanTemplates',
			hasAccess: hasProjectPlan
		},
		{
			path: '/admin/products',
			Component: AdminProducts,
			oldRouteState: 'products',
			hasAccess: isAdmin,
			queryParams: '?import'
		},
		{
			path: '/admin/customer-support/fields',
			Component: AdminTicketFields,
			oldRouteState: 'ticketFields',
			hasAccess: hasSupportAccess
		},
		{
			path: '/admin/import/:id',
			Component: PortedImport,
			oldRouteState: 'import',
			hasAccess: isAdmin
		},
		{
			path: '/admin/import',
			Component: PortedImports,
			oldRouteState: 'imports',
			hasAccess: hasImportsAccess,
			exact: true
		},
		{
			path: '/admin/sign-in-stats',
			Component: LoginStats,
			oldRouteState: 'loginStats',
			hasAccess: isAdmin
		},
		{
			path: '/admin/journeyStatuses',
			Component: JourneyStatuses,
			oldRouteState: 'journeyStatuses',
			hasAccess: isAdmin && journeyStatusAvailable
		},
		{
			path: '/admin/customer-support/responsetemplates',
			Component: AdminTicketResponseTemplates,
			oldRouteState: 'ticketResponseTemplates',
			hasAccess: hasSupportAccess
		},
		{
			path: '/admin/appointment-availability',
			Component: EasyBookingSettings,
			oldRouteState: 'appointmentAvailability',
			hasAccess: true
		},
		{
			path: '/admin/booked-first-appointment-goals',
			Component: BookedFirstAppointmentGoals,
			oldRouteState: 'bookedfirstappointmentgoals',
			hasAccess: hasCrm && (isAdmin || isTeamLeader) && hasGoals
		},
		{
			path: '/admin/dashboard',
			Component: AdminDashboard,
			hasAccess: true,
			oldRouteState: 'dashboard'
		},
		{
			path: '/admin/journeyEditor/:type/:id?',
			Component: JourneyEditor,
			oldRouteState: 'journeyEditor',
			hasAccess: journeyStatusAvailable && isAdmin
		},
		{
			path: '/admin/reminders',
			Component: Reminders,
			hasAccess: true,
			oldRouteState: 'reminders'
		},
		{
			path: '/admin/email-signatures',
			Component: AdminEmailSignatures,
			hasAccess: true,
			oldRouteState: 'defaultMailSignature'
		},
		{
			path: '/admin/pipeline-goals',
			Component: AdminPipelineGoals,
			hasAccess: (isAdmin || isTeamleader) && hasCrm,
			oldRouteState: 'pipelinegoals'
		},
		{
			path: '/admin/agreement',
			Component: AdminAgreements,
			hasAccess: isAdmin && hasCrm && hasRecurringOrder,
			oldRouteState: 'agreement'
		},
		{
			path: '/admin/sharedviews',
			Component: SharedViews,
			hasAccess: isAdmin && hasSharedViews,
			oldRouteState: 'sharedviews'
		},
		{
			path: '/admin/mailSignature',
			Component: MailSignature,
			hasAccess: isAdmin && hasMailSignature,
			oldRouteState: 'mailsignature'
		},
		{
			path: '/admin/documentTemplates',
			Component: AdminDocumentTemplates,
			hasAccess: isAdmin,
			oldRouteState: 'documentTemplates'
		},
		{
			path: '/admin/pricelists',
			Component: PriceLists,
			hasAccess: hasCrm && isAdmin,
			oldRouteState: 'priceLists'
		},
		{
			path: '/admin/activeInvites',
			Component: AdminActiveInvites,
			hasAccess: isAdmin,
			oldRouteState: 'activeInvites'
		},
		{
			path: '/admin/optOut',
			Component: AdminOptOut,
			hasAccess: isAdmin || isMailAdmin,
			oldRouteState: 'optOut'
		},
		{
			path: '/admin/notifications',
			Component: AdminNotifications,
			hasAccess: true,
			oldRouteState: 'notificationCenter'
		},
		{
			path: '/admin/booked-appointment-goals',
			Component: BookedAppointmentGoals,
			hasAccess: (isAdmin || isTeamLeader) && hasCrm,
			oldRouteState: 'bookedappointmentgoals'
		},
		{
			path: '/admin/listsalesboards',
			Component: Salesboard,
			hasAccess: isAdmin || hasCrm,
			oldRouteState: 'listSalesboards'
		},
		{
			path: '/admin/salesboard-card',
			Component: SalesboardCard,
			hasAccess: isAdmin && hasCrm,
			oldRouteState: 'salesboardCard'
		},
		{
			path: '/admin/profile',
			Component: AdminProfile,
			hasAccess: true,
			oldRouteState: 'profile'
		},
		{
			path: '/admin/triggers',
			Component: AdminTriggers,
			hasAccess: (isAdmin && hasTriggersFeature) || isProjectAdmin,
			oldRouteState: 'triggers'
		},
		{
			path: '/admin/anonymization',
			Component: AdminAnonymization,
			hasAccess: true,
			oldRouteState: 'anonymization'
		},
		{
			path: '/admin/fields/:type?',
			Component: AdminFields,
			hasAccess:
				(isAdmin && hasCrm) ||
				(isSupportAdmin && hasSupportAccess) ||
				(hasProjectAdminAccess && hasProjectPlan),
			oldRouteState: 'fields'
		},
		{
			path: '/admin/automations',
			Component: ListAutomations,
			hasAccess: automationsAvailable && isAdmin,
			oldRouteState: 'automations'
		},
		{
			path: '/admin/activitytypes',
			Component: ActivityTypes,
			oldRouteState: 'activityTypes',
			hasAccess: isAdmin && hasCrm
		},
		{
			path: '/admin/categories/:type?',
			Component: AdminCategories,
			oldRouteState: 'category',
			hasAccess: isAdmin && hasCrm
		},
		{
			path: '/admin/completed-first-appointment-goals',
			Component: CompletedFirstAppointmentGoals,
			hasAccess: hasCrm && (isAdmin || isTeamLeader) && hasCompletedFirstAppointmentGoalsAcess,
			oldRouteState: 'completedfirstappointmentgoals'
		},
		{
			path: '/admin/scripts',
			Component: AdminScripts,
			hasAccess: isAdmin,
			oldRouteState: 'scripts'
		},
		{
			path: '/admin/script/:id?',
			Component: AdminScript,
			hasAccess: isAdmin,
			oldRouteState: 'script'
		},
		{
			path: '/admin/orderstages',
			Component: OrderStages,
			hasAccess: isAdmin && hasCrm && hasStagesAdminFeature,
			oldRouteState: 'orderstages'
		},
		{
			path: '/admin/automation/:id?',
			Component: EditAutomation,
			hasAccess: automationsAvailable && isAdmin,
			oldRouteState: 'automation'
		},
		{
			path: '/admin/looker',
			Component: AdminLooker,
			hasAccess: isAdmin && hasLookerFeature,
			oldRouteState: 'looker'
		},
		{
			path: '/admin/prospecting-signals',
			Component: ProspectingSignals,
			hasAccess: isAdmin && hasCrm && hasProspectingPro && hasProspectingSignals,
			oldRouteState: 'prospectingSignals'
		},
		{
			path: '/admin/mailsettings',
			Component: MailSettings,
			hasAccess: isAdmin || isMailAdmin,
			oldRouteState: 'mailSettings'
		},
		{
			path: '/admin/usersAndRoles',
			Component: AdminUsersAndRoles,
			hasAccess: isAdmin,
			oldRouteState: 'usersAndRoles'
		},
		{
			path: '/admin/security/apiKeys',
			Component: AdminApiKeys,
			hasAccess: isAdmin && hasApiKeys,
			oldRouteState: 'apiKeys'
		},
		{
			path: '/admin/security/2fa',
			Component: Admin2fa,
			hasAccess: isAdmin && hasSmsAuth,
			oldRouteState: '2fa'
		},
		{
			path: '/admin/marketRejectlist',
			Component: MarketRejectList,
			hasAccess: isAdmin || isMailAdmin,
			oldRouteState: 'marketRejectlist'
		},
		{
			path: '/admin/currencies',
			Component: AdminCurrencies,
			hasAccess: isAdmin,
			oldRouteState: 'currencies'
		},
		{
			path: '/admin/userdefinedobjects',
			Component: UserDefinedObjects,
			hasAccess: isAdmin,
			oldRouteState: 'userdefinedobjects'
		},
		{
			path: '/admin/billing/:initialTab?',
			Component: AdminBilling,
			hasAccess: isBillingAdmin,
			oldRouteState: 'billing'
		},
		{
			path: '/admin/optin',
			Component: OptIns,
			hasAccess: hasOptIn,
			oldRouteState: 'optIn'
		},
		{
			path: '/admin/integrations/edit/:id?',
			Component: AdminEditIntegration,
			hasAccess: true,
			oldRouteState: 'editIntegration'
		},
		{
			path: '/admin/integrations/configure/:id/:configure?',
			Component: AdminConfigureIntegration,
			hasAccess: hasConfigureIntegrationAccess,
			oldRouteState: 'integration'
		},
		{
			path: '/admin/integrations',
			Component: AdminIntegrations,
			hasAccess: true,
			oldRouteState: 'integrations'
		},
		{
			path: '/admin/visit',
			Component: VisitSettings,
			hasAccess: isAdmin || isMailAdmin,
			oldRouteState: 'visitSettings'
		},
		{
			path: '/admin/brands',
			Component: Brands,
			hasAccess: isAdmin && hasCrm && hasBrandsFeature,
			oldRouteState: 'brand'
		},
		{
			path: '/admin/oneoff-quota',
			Component: OneOff,
			hasAccess: (isAdmin || isTeamLeader) && hasCrm,
			oldRouteState: 'oneoffquota'
		},
		{
			path: '/admin/subscription-quota',
			Component: SubscriptionQuota,
			hasAccess: (isAdmin || isTeamLeader) && hasCrm,
			oldRouteState: 'subscriptionquota'
		},
		{
			path: '/admin/sales-margin-quota',
			Component: SalesMarginQuota,
			hasAccess: (isAdmin || isTeamLeader) && hasCrm,
			oldRouteState: 'salesmarginquota'
		},
		{
			path: '/admin/sales-quota',
			Component: AdminSalesQuota,
			hasAccess: (isAdmin || isTeamLeader) && hasCrm,
			oldRouteState: 'salesquota'
		},
		{
			path: '/admin/product-quota',
			Component: ProductQuota,
			hasAccess: (isAdmin || isTeamLeader) && hasCrm,
			oldRouteState: 'productquota'
		},
		{
			path: '/admin/accountProfile',
			Component: AccountProfile,
			hasAccess: isAdmin || isMailAdmin,
			oldRouteState: 'accountProfile'
		},
		{
			path: '/admin/ip-block',
			Component: AdminIpBlock,
			hasAccess: isAdmin,
			oldRouteState: 'ipblock'
		},
		{
			path: '/admin/shared-appointment-availability/:id',
			Component: AdminSharedEasyBookingSettings,
			hasAccess: (isAdmin || isMailAdmin) && hasCrm,
			oldRouteState: 'sharedAppointmentAvailabilitySettings'
		},
		{
			path: '/admin/shared-appointment-availability',
			Component: AdminSharedEasyBooking,
			hasAccess: (isAdmin || isMailAdmin) && hasCrm,
			oldRouteState: 'sharedAppointmentAvailability'
		},
		{
			path: '/admin/activity-quota',
			Component: AdminActivityQuota,
			hasAccess: shouldShowOldActivityQuota,
			oldRouteState: 'activityquota'
		},
		{
			path: '/admin/accountsettings',
			Component: AdminAccountsSettings,
			hasAccess: (hasNewFieldsAccess || hasBrokenFiscalYearAccess) && (isMailAdmin || isAdmin),
			oldRouteState: 'accountsettings'
		},
		{
			path: '/admin/appointment-editor/:id',
			Component: AdminAppointmentEditor,
			hasAccess: true,
			oldRouteState: 'appointmentEditor'
		},
		{
			path: '/admin/salesboards',
			Component: Salesboards,
			hasAccess: isAdmin && hasCrm,
			oldRouteState: 'salesboards'
		},
		{
			path: '/admin/customer-support/autoreplytemplates',
			Component: AdminTicketAutoReplyTemplates,
			oldRouteState: 'ticketAutoReplyTemplates',
			hasAccess: hasSupportAccess
		},
		{
			path: '/admin/deletelog',
			Component: DeleteLog,
			hasAccess: isAdmin,
			oldRouteState: 'deleteLog'
		},
		{
			path: '/admin/salesboard/:id?',
			Component: AdminSalesBoard,
			hasAccess: hasCrm && isAdmin,
			oldRouteState: 'salesboard'
		},
		{
			path: '/admin/completed-appointment-goals',
			Component: AdminCompletedAppointmentGoals,
			hasAccess: (isAdmin || isTeamLeader) && hasCrm,
			oldRouteState: 'completedappointmentgoals'
		},
		{
			path: '/admin/dynamiclinks',
			Component: DynamicLinks,
			hasAccess: isAdmin && hasDynamicLinks,
			oldRouteState: 'dynamiclinks'
		},
		{
			path: '/admin/phonecall-goals',
			Component: AdminPhoneCallGoals,
			hasAccess: (isAdmin || isTeamLeader) && hasCrm,
			oldRouteState: 'phonecallgoals'
		},
		{
			path: '/admin/trigger/:id?',
			Component: AdminEditTrigger,
			hasAccess: (isAdmin && hasTriggersFeature) || isProjectAdmin,
			oldRouteState: 'trigger'
		},
		{
			path: '/admin/user/:id?',
			Component: UserRoute,
			hasAccess: hasAdminUserAccess && isAdmin,
			oldRouteState: 'user'
		},
		{
			path: '/admin/credit-monitoring',
			Component: CreditMonitoring,
			hasAccess: isAdmin && hasProspectingPro,
			oldRouteState: 'creditMonitoring'
		},
		{
			path: '/admin/ai-prompts',
			Component: AdminAIPrompts,
			hasAccess: isAdmin && hasAiFeature,
			oldRouteState: 'aiPrompts'
		},
		{
			path: '/admin/opportunity-risks',
			Component: AdminOpportunityRisks,
			hasAccess: isAdmin && hasOpportunityRisks,
			oldRouteState: 'opportunityRisks'
		},
		{
			path: '/admin/security/mcp',
			Component: AdminMcpSecurity,
			hasAccess: isAdmin && hasMcpConnections && hasMcpSoftDeploy,
			oldRouteState: 'mcpSecurity'
		}
	].filter(route => route.hasAccess);

	return routes;
};
export default useAdminRoutes;
